import { segmented } from "@web/theme/theme";
import { useEffect, useRef } from "react";

export type Segment<K extends string = string> = { key: K; label: string };

export type SegmentedControlProps<K extends string> = {
  items: readonly Segment<K>[];
  value: K;
  onChange: (key: K) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  /** Record workspaces may have more sections than a phone can show at once. */
  scrollable?: boolean;
};

/** iOS-style segmented control (tabs inside a screen). */
export function SegmentedControl<K extends string>({ items, value, onChange, label = "Sections", className = "", disabled = false, scrollable = false }: SegmentedControlProps<K>) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollable) return;
    const container = ref.current, selected = container?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!container || !selected) return;
    const edge = selected.offsetLeft - container.offsetLeft;
    if (edge < container.scrollLeft) container.scrollLeft = edge;
    else if (edge + selected.offsetWidth > container.scrollLeft + container.clientWidth) container.scrollLeft = edge + selected.offsetWidth - container.clientWidth;
  }, [value, scrollable]);
  return (
    <div ref={ref} role="tablist" aria-label={label} className={`${segmented.base} ${scrollable ? "v365-segmented-scroll" : ""} ${className}`.trim()}>
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button key={it.key} type="button" role="tab" aria-selected={active} disabled={disabled} onClick={() => onChange(it.key)} className={`${segmented.item} ${active ? segmented.active : segmented.inactive}`}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
